import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  bulkCreateWorkEntries,
  clearErrors,
  clearMessage,
  resetSuccess,
} from "../../redux/workEntrySlice";
import { getAllEmps } from "../../redux/empSlice";
import { getAllEmpWorks } from "../../redux/empworkSlice";
import SearchableSelect from "../common/SearchableSelect";
import { Helmet } from "react-helmet-async";

const DailyWorkEntry = () => {
  const dispatch = useDispatch();
  const { loading, error, success, message } = useSelector(
    (state) => state.workEntry
  );
  const { emps } = useSelector((state) => state.emp);
  const { empworks } = useSelector((state) => state.empwork);

  const [workDate, setWorkDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [employeeError, setEmployeeError] = useState("");
  const [rows, setRows] = useState([
    { workId: "", quantity: "", rate: 0, total: 0 },
  ]);
  const [rowErrors, setRowErrors] = useState({});

  // Load employees and work types
  useEffect(() => {
    dispatch(getAllEmps());
    dispatch(getAllEmpWorks());
  }, [dispatch]);

  // Clear messages
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(clearErrors());
        dispatch(clearMessage());
        dispatch(resetSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  // Calculate rate and total when work type changes
  useEffect(() => {
    if (empworks.length === 0) return;
    
    setRows((prevRows) => {
      return prevRows.map((row) => {
        const work = empworks.find((w) => w._id === row.workId);
        const rate = work ? work.amount : row.rate || 0;
        const quantity = parseFloat(row.quantity) || 0;
        const total = parseFloat((rate * quantity).toFixed(2));

        return { ...row, rate, total };
      });
    });
  }, [empworks]);

  // Handle employee change
  const handleEmployeeChange = (value) => {
    setSelectedEmployee(value);
    setEmployeeError("");
  };

  // Handle row change
  const handleRowChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index] = { ...updatedRows[index], [field]: value };

    // Calculate rate and total
    if (field === "workId") {
      const work = empworks.find((w) => w._id === value);
      updatedRows[index].rate = work ? work.amount : 0;
      updatedRows[index].total = parseFloat(
        (updatedRows[index].rate * (parseFloat(updatedRows[index].quantity) || 0)).toFixed(2)
      );
    } else if (field === "quantity") {
      const quantity = parseFloat(value) || 0;
      updatedRows[index].quantity = value;
      updatedRows[index].total = parseFloat(
        (updatedRows[index].rate * quantity).toFixed(2)
      );
    }

    // Auto-add a new row when the last row is completed (work + quantity)
    const lastRowFilled =
      updatedRows[index].workId &&
      parseFloat(updatedRows[index].quantity) > 0 &&
      index === rows.length - 1;
    if (lastRowFilled) {
      updatedRows.push({ workId: "", quantity: "", rate: 0, total: 0 });
    }

    setRows(updatedRows);

    // Clear error for this row and field
    const errorKey = `${index}-${field}`;
    setRowErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });
  };

  // Add new row
  const handleAddRow = () => {
    setRows([
      ...rows,
      { workId: "", quantity: "", rate: 0, total: 0 },
    ]);
  };

  // Remove row
  const handleRemoveRow = (index) => {
    if (rows.length > 1) {
      const updatedRows = rows.filter((_, i) => i !== index);
      setRows(updatedRows);
    }
  };

  // Validate rows
  const validateRows = () => {
    let isValid = true;
    const errors = {};

    // Validate employee selection
    if (!selectedEmployee) {
      setEmployeeError("Employee is required");
      isValid = false;
    } else {
      setEmployeeError("");
    }

    // Validate rows
    rows.forEach((row, index) => {
      if (!row.workId) {
        errors[`${index}-workId`] = "Work type is required";
        isValid = false;
      }
      if (!row.quantity || parseFloat(row.quantity) <= 0) {
        errors[`${index}-quantity`] = "Quantity must be greater than 0";
        isValid = false;
      }
    });

    setRowErrors(errors);
    return isValid;
  };

  // Handle save
  const handleSave = async (e) => {
    e.preventDefault();

    if (!validateRows()) {
      return;
    }

    const entries = rows.map((row) => ({
      employeeId: selectedEmployee,
      workId: row.workId,
      quantity: parseFloat(row.quantity),
    }));

    try {
      await dispatch(bulkCreateWorkEntries({ date: workDate, entries })).unwrap();
      // Reset form
      setRows([
        { workId: "", quantity: "", rate: 0, total: 0 },
      ]);
      setRowErrors({});
      setSelectedEmployee("");
      setEmployeeError("");
    } catch (error) {
      // Error handled by Redux
    }
  };

  return (
    <>
      <Helmet>
        <title>Daily Work Entry - RTS Books</title>
      </Helmet>
      <style>{`
        .daily-work-entry-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 50%, #c7d2fe 100%);
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .daily-work-entry-wrapper {
          max-width: 1400px;
          margin: 0 auto;
        }

        .daily-work-entry-header {
          text-align: center;
          margin-bottom: 30px;
          color: #1e40af;
        }

        .daily-work-entry-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          color: #1e40af;
          letter-spacing: -0.5px;
        }

        .daily-work-entry-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(30, 64, 175, 0.08);
          border: 1px solid #e0e7ff;
        }

        .date-selector {
          margin-bottom: 30px;
        }

        .employee-selector {
          margin-bottom: 30px;
        }

        .date-selector label,
        .employee-selector label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #1e293b;
          font-size: 0.95rem;
        }

        .date-selector input {
          padding: 12px 16px;
          border: 2px solid #cbd5e1;
          border-radius: 8px;
          font-size: 1rem;
          width: 100%;
          max-width: 300px;
          color: #1e293b;
        }

        .date-selector input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .employee-selector .searchable-select-container {
          max-width: 400px;
        }

        .work-entries-table-wrapper {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          margin: 20px 0;
          padding: 0;
          border-radius: 12px;
          border: 1px solid #e0e7ff;
          background: #ffffff;
        }

        .work-entries-table {
          width: 100%;
          min-width: 900px;
          border-collapse: collapse;
          margin: 0;
        }

        .work-entries-table th,
        .work-entries-table td {
          padding: 14px 12px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: middle;
        }

        .work-entries-table th {
          background: #1e40af;
          font-weight: 600;
          color: white;
          position: sticky;
          top: 0;
          z-index: 10;
          white-space: nowrap;
          font-size: 0.9rem;
        }

        .work-entries-table tbody tr {
          transition: background 0.2s ease;
        }

        .work-entries-table tbody tr:hover {
          background: #f8fafc;
        }

        .work-entries-table tbody tr:nth-child(even) {
          background: #fafbfc;
        }

        .work-entries-table tbody tr:nth-child(even):hover {
          background: #f8fafc;
        }

        .work-entries-table td {
          background: transparent;
        }

        .work-entries-table td > div {
          min-width: 150px;
        }

        .work-entries-table input {
          width: 100%;
          padding: 10px 12px;
          border: 2px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.95rem;
          min-width: 100px;
          color: #1e293b;
          background: #ffffff;
        }

        .work-entries-table input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .work-entries-table input.error {
          border-color: #dc3545;
        }

        .work-entries-table .searchable-select-container {
          min-width: 180px;
        }

        .error-text {
          color: #dc3545;
          font-size: 0.8rem;
          margin-top: 6px;
          display: block;
          font-weight: 500;
        }

        .rate-cell,
        .total-cell {
          font-weight: 600;
          color: #1e40af;
          white-space: nowrap;
          text-align: right;
          padding-right: 16px;
          font-size: 0.95rem;
        }

        .work-entries-table td:has(.btn-danger) {
          text-align: center;
          vertical-align: middle;
        }

        .work-entries-table .btn-danger {
          padding: 8px 14px;
          font-size: 0.85rem;
          white-space: nowrap;
          min-width: auto;
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .btn-primary {
          background: #3b82f6;
          color: white;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
        }

        .btn-primary:hover {
          background: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-danger {
          background: #dc3545;
          color: white;
          padding: 8px 12px;
          font-size: 0.85rem;
        }

        .btn-success {
          background: #1e40af;
          color: white;
          box-shadow: 0 2px 8px rgba(30, 64, 175, 0.2);
        }

        .btn-success:hover {
          background: #1e3a8a;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(30, 64, 175, 0.3);
        }

        .btn-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
          flex-wrap: wrap;
        }

        .message {
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 0.95rem;
        }

        .message-success {
          background: #d4edda;
          color: #155724;
        }

        .message-error {
          background: #f8d7da;
          color: #721c24;
        }

        /* Tablet Responsive */
        @media (max-width: 1024px) {
          .daily-work-entry-container {
            padding: 15px;
          }

          .daily-work-entry-card {
            padding: 20px;
          }

          .daily-work-entry-title {
            font-size: 2rem;
          }

          .work-entries-table-wrapper {
            margin: 15px 0;
            padding: 0;
            border-radius: 10px;
          }

          .work-entries-table {
            min-width: 800px;
          }

          .work-entries-table th,
          .work-entries-table td {
            padding: 12px 10px;
          }
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .daily-work-entry-container {
            padding: 10px;
          }

          .daily-work-entry-card {
            padding: 15px;
            border-radius: 15px;
          }

          .daily-work-entry-header {
            margin-bottom: 20px;
          }

          .daily-work-entry-title {
            font-size: 1.8rem;
            margin-bottom: 8px;
          }

          .date-selector,
          .employee-selector {
            margin-bottom: 20px;
          }

          .date-selector label,
          .employee-selector label {
            font-size: 0.9rem;
            margin-bottom: 6px;
          }

          .date-selector input {
            padding: 10px 0px;
            font-size: 0.95rem;
            max-width: 100%;
          }

          .employee-selector .searchable-select-container {
            max-width: 100%;
          }

          .work-entries-table-wrapper {
            margin: 15px -15px;
            padding: 0;
            border-left: none;
            border-right: none;
            border-radius: 0;
          }

          .work-entries-table {
            min-width: 750px;
            font-size: 0.85rem;
          }

          .work-entries-table th,
          .work-entries-table td {
            padding: 10px 8px;
          }

          .work-entries-table th {
            font-size: 0.8rem;
            padding: 12px 8px;
          }

          .work-entries-table input {
            padding: 8px 10px;
            font-size: 0.85rem;
            min-width: 90px;
          }

          .work-entries-table .searchable-select-container {
            min-width: 140px;
          }

          .work-entries-table .searchable-select-input {
            padding: 8px 0px 8px 0px;
            font-size: 0.85rem;
          }

          .error-text {
            font-size: 0.75rem;
          }

          .rate-cell,
          .total-cell {
            font-size: 0.85rem;
          }

          .btn {
            padding: 10px 16px;
            font-size: 0.9rem;
            flex: 1;
            min-width: calc(50% - 5px);
          }

          .btn-danger {
            padding: 6px 10px;
            font-size: 0.75rem;
            min-width: auto;
            flex: none;
          }

          .btn-actions {
            gap: 8px;
            margin-top: 15px;
          }

          .message {
            padding: 12px;
            font-size: 0.9rem;
            margin-bottom: 15px;
          }
        }

        /* Small Mobile */
        @media (max-width: 480px) {
          .daily-work-entry-container {
            padding: 8px;
          }

          .daily-work-entry-card {
            padding: 12px;
          }

          .daily-work-entry-title {
            font-size: 1.5rem;
          }

          .date-selector,
          .employee-selector {
            margin-bottom: 15px;
          }

          .work-entries-table-wrapper {
            margin: 12px -12px;
            padding: 0;
            border-left: none;
            border-right: none;
            border-radius: 0;
          }

          .work-entries-table {
            min-width: 650px;
            font-size: 0.8rem;
          }

          .work-entries-table th,
          .work-entries-table td {
            padding: 8px 6px;
            font-size: 0.8rem;
          }

          .work-entries-table th {
            font-size: 0.75rem;
            padding: 10px 6px;
          }

          .work-entries-table input {
            padding: 6px 0px;
            font-size: 0.8rem;
            min-width: 80px;
          }

          .work-entries-table .searchable-select-container {
            min-width: 120px;
          }

          .work-entries-table .searchable-select-input {
            padding: 7px 0px 10px 0px;
            font-size: 0.8rem;
          }

          .btn {
            padding: 10px 14px;
            font-size: 0.85rem;
            min-width: calc(50% - 4px);
          }

          .btn-danger {
            padding: 5px 8px;
            font-size: 0.7rem;
          }
        }

        /* Extra Small Mobile */
        @media (max-width: 360px) {
          .daily-work-entry-title {
            font-size: 1.3rem;
          }

          .work-entries-table {
            min-width: 600px;
          }

          .btn {
            font-size: 0.8rem;
            padding: 8px 12px;
            min-width: 100%;
          }

          .btn-danger {
            min-width: auto;
            width: auto;
          }

          .btn-actions {
            flex-direction: column;
          }

          .btn-actions .btn {
            width: 100%;
          }
        }
      `}</style>

      <div className="daily-work-entry-container">
        <div className="daily-work-entry-wrapper">
          <div className="daily-work-entry-header">
            <h1 className="daily-work-entry-title">Daily Work Entry</h1>
          </div>

          <div className="daily-work-entry-card">
            {error && (
              <div className="message message-error">{error}</div>
            )}

            {success && message && (
              <div className="message message-success">{message}</div>
            )}

            <form onSubmit={handleSave}>
              <div className="date-selector">
                <label htmlFor="workDate">Work Date *</label>
                <input
                  id="workDate"
                  type="date"
                  value={workDate}
                  onChange={(e) => setWorkDate(e.target.value)}
                  required
                />
              </div>

              <div className="employee-selector">
                <label htmlFor="employee">Employee *</label>
                <SearchableSelect
                  name="employeeId"
                  options={emps}
                  value={selectedEmployee}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                  placeholder="Search employee..."
                  getOptionLabel={(emp) => `${emp.name} (${emp.number})`}
                  getOptionValue={(emp) => emp._id}
                  searchFields={["name", "number", "_id"]}
                  className={employeeError ? "error" : ""}
                  error={!!employeeError}
                  required
                  disabled={loading || !workDate}
                />
                {employeeError && (
                  <div className="error-text">{employeeError}</div>
                )}
              </div>

              <div className="work-entries-table-wrapper">
                <table className="work-entries-table">
                  <thead>
                    <tr>
                      <th>Quantity *</th>
                      <th>Work Type *</th>
                      <th>Rate</th>
                      <th>Total</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={index}>
                         <td style={{ minWidth: "120px" }}>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={row.quantity}
                            onChange={(e) =>
                              handleRowChange(index, "quantity", e.target.value)
                            }
                            className={
                              rowErrors[`${index}-quantity`] ? "error" : ""
                            }
                            placeholder="0.00"
                            required
                            disabled={!selectedEmployee}
                          />
                          {rowErrors[`${index}-quantity`] && (
                            <div className="error-text">
                              {rowErrors[`${index}-quantity`]}
                            </div>
                          )}
                        </td>
                        <td style={{ minWidth: "180px" }}>
                          <SearchableSelect
                            name="workId"
                            options={empworks}
                            value={row.workId}
                            onChange={(e) =>
                              handleRowChange(index, "workId", e.target.value)
                            }
                            placeholder="Search work type..."
                            getOptionLabel={(work) => `${work.name} - ₹${parseFloat(work.amount).toFixed(2)}`}
                            getOptionValue={(work) => work._id}
                            searchFields={["name", "_id"]}
                            className={
                              rowErrors[`${index}-workId`] ? "error" : ""
                            }
                            error={!!rowErrors[`${index}-workId`]}
                            required
                            disabled={loading || !selectedEmployee}
                          />
                          {rowErrors[`${index}-workId`] && (
                            <div className="error-text">
                              {rowErrors[`${index}-workId`]}
                            </div>
                          )}
                        </td>
                       
                        <td className="rate-cell" style={{ minWidth: "100px" }}>₹{row.rate.toFixed(2)}</td>
                        <td className="total-cell" style={{ minWidth: "100px" }}>₹{row.total.toFixed(2)}</td>
                        <td style={{ minWidth: "90px", textAlign: "center" }}>
                          {rows.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => handleRemoveRow(index)}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="btn-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAddRow}
                  disabled={!selectedEmployee}
                >
                  ➕ Add Row
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={loading || !selectedEmployee}
                >
                  {loading ? "Saving..." : "💾 Save All Entries"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default DailyWorkEntry;
